import {
  Baseline,
  OneOfElements,
  Project,
  ProjectMetadata,
} from '../../models/project.model';
import { BuildingDesign } from '../../models/design/building-design.model';
import {
  EnergyActivity,
  TAP_WATER_RECIRCULATION_LOSS,
  DEFAULT_LIMIT_TEMP,
  TAPWATER_HEATING_DEMAND,
  SPACE_DEMAND_CATEGORIES,
} from './energy.models';

import { getBuildingGFA } from '../expression-variables.helpers';
import {
  getSpaceDemandInWhPerM2,
  getSpaceDemandOptionsFromVersion,
} from './space-demand.helpers';
import { EnvironmentHourData } from './weather.models';
import { convert } from '../conversion.helpers';
import { getHeatedAreaFromEnergyActivities } from './energy-activity.helpers';
import { logSpaceDemand } from './space-demand-logger.helper';
import { HOURS_PER_YEAR } from '@shared/constants/math.constants';
import { getEntries } from '../object.helpers';
import { PartialRecord } from '@shared/models/type-helpers.model';
import { guards } from '../typeguards.helpers';
import { labelFromValue } from '../string.helpers';
import { IProposal } from '@shared/models/proposals.model';
import { getResolvedBuildingVersion } from '../actions/resolved-version.helpers';
import { flattenElements } from '../recursive-element.helpers';
import { findElement } from '../recursive-element.helpers';
import { isElementOfCategory } from '../element-category.helpers';
import { isTemporaryElementId } from '../element.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { ResidentialProgramType } from '@shared/models/design/activities/properties';
import { projectGuards } from '../guards/project-guards.helpers';
import { cacheFactory } from '../function.helpers';

type IHeatingDemandResult = {
  /**
   * Heating losses from the transmission of the building envelope
   */

  transmissionLosses: number;

  /**
   * Heating losses from the tap water recirculation in kWh
   */
  tapWaterRecirculationLosses: number;

  /**
   * The amount of recirculation losses that can be considered a gain
   */
  tapWaterRecirculationGain: number;
  /**
   * Heating losses from the transmission of the building envelope
   */

  ventilationLosses: number;
  /**
   * Heating losses from the airing of the building
   */
  airingHeatingLosses: number;

  /**
   * Heating losses from the air leakage of the building
   */
  airLeakageLosses: number;
  /**
   * Heating from the activity (e.g. households, offices, etc.)
   */

  activityHeating: number;
  /**
   * Heating from the occupants (people) in the building
   */

  occupantHeating: number;
  /**
   * Heating from the solar insolation (windows / balcony doors etc)
   */
  solarInsolationHeating: number;

  /**
   * Heating demand from tapwater in kWh
   */
  tapwaterHeatingDemand: number;

  /**
   * Total heating demand in kWh
   */
  heatingDemand: number;

  /**
   * Post heating demand in kWh
   */
  postHeatingDemand: number;

  /**
   * Total delivered cooling demand in kWh (terminal cooling + useful coil cooling)
   */
  coolingDemand: number;

  /**
   * Pre-cooling coil energy in kWh
   */
  preCoolingDemand: number;

  /**
   * Limit temperature used to calculate the degree hours
   */
  limitTemp: number;
};

/**
 * If somethinng goes wrong (like NaN values due to invalid input) we return this type
 */
type HeatingDemandResultsWithErrors = IHeatingDemandResult & {
  /**
   * Record of field names that contained NaN values and were set to 0, with error messages
   */
  errors: PartialRecord<keyof IHeatingDemandResult, string>;
};

const emptyHeatingDemandResult: IHeatingDemandResult = Object.freeze({
  transmissionLosses: 0,
  tapWaterRecirculationLosses: 0,
  ventilationLosses: 0,
  airingHeatingLosses: 0,
  airLeakageLosses: 0,
  activityHeating: 0,
  occupantHeating: 0,
  solarInsolationHeating: 0,
  tapwaterHeatingDemand: 0,
  tapWaterRecirculationGain: 0,
  heatingDemand: 0,
  postHeatingDemand: 0,
  coolingDemand: 0,
  preCoolingDemand: 0,
  limitTemp: DEFAULT_LIMIT_TEMP,
});

/**
 * Helper function to validate heating demand result values and track errors
 * @param values - Object containing the calculated values
 * @returns Object with cleaned values (NaN replaced with 0) and record of error field names with messages
 */
const validateHeatingDemandValues = (
  values: IHeatingDemandResult,
): HeatingDemandResultsWithErrors | IHeatingDemandResult => {
  const errors: HeatingDemandResultsWithErrors['errors'] = {};
  const cleanedValues: IHeatingDemandResult = { ...values };

  // Check each field for NaN values store error in record and set value to 0
  for (const [key, value] of getEntries(cleanedValues)) {
    if (!guards.is.finiteNumber(value)) {
      errors[key] =
        `Value "${labelFromValue(key)}" could not be calculated correctly due to invalid input. Check that your climate shell dimensions seems reasonable.`;
      cleanedValues[key] = 0;
    }
  }

  // If there are errors, return the cleaned values with the errors
  return Object.keys(errors).length > 0
    ? {
        ...cleanedValues,
        errors,
      }
    : values;
};

/** Input for {@link getBuildingHeatingDemand} */
type BuildingHeatingDemandOptions = {
  /** Legacy project metadata (only used when design is omitted) */
  metadata?: ProjectMetadata;
  /** Environment hour data (8760 hours) */
  environmentHours: EnvironmentHourData[];
  /** Fallback Atemp (m²) when design activities yield no heated area */
  heatedArea?: number;
  /** Building design. When provided, activities and areas come from design instead of metadata. */
  design?: BuildingDesign;
  /** When provided, envelope/transmission uses only elements active in this proposal (for per-proposal heat demand) */
  proposal?: IProposal;
};

/** Options for {@link getTapwaterHeatingDemandByActivity} residential rate selection */
export type TapwaterHeatingDemandOptions = {
  /** Residential program subtype from design; single-family uses the small-house BEN rate */
  residentialProgramType?: ResidentialProgramType;
};

/**
 * Get how much heating the building requires every year.
 * @param version - The version of the building
 * @param options - See {@link BuildingHeatingDemandOptions}
 * @returns The heating demand for the building
 */
const computeBuildingHeatingDemand = (
  version: Baseline,
  {
    metadata,
    environmentHours,
    heatedArea,
    design,
    proposal,
  }: BuildingHeatingDemandOptions,
): IHeatingDemandResult | HeatingDemandResultsWithErrors => {
  const ventilation = {
    postHeatingStrategy: 'alwaysMinSAT' as const,
    fanHeatRiseK: 0.5,
  };

  const spaceDemandBase = {
    environmentHours,
    ventilation,
    heatedArea,
    proposal,
  };

  let spaceDemandOptions;
  if (design) {
    spaceDemandOptions = getSpaceDemandOptionsFromVersion(version, {
      ...spaceDemandBase,
      design,
    });
  } else if (metadata) {
    spaceDemandOptions = getSpaceDemandOptionsFromVersion(version, {
      ...spaceDemandBase,
      metadata,
    });
  } else {
    throw new Error('metadata or design must be provided');
  }

  const activities = spaceDemandOptions.activities;

  const result = getSpaceDemandInWhPerM2(spaceDemandOptions);

  const { annualWhPerM2 } = result.building;

  const calculatedHeatedArea = getHeatedAreaFromEnergyActivities(activities);
  const atemp =
    calculatedHeatedArea > 0 ? calculatedHeatedArea : (heatedArea ?? 0);

  logSpaceDemand(result, version);

  /** Convert Wh/m2 to kWh */
  const toKwh = (wh: number) => convert(wh, 'Wh', 'kWh') * atemp;

  // Validate values and handle NaN cases
  return validateHeatingDemandValues({
    transmissionLosses: toKwh(annualWhPerM2.transmissionHeat),
    ventilationLosses: toKwh(annualWhPerM2.ventilationHeat),
    airingHeatingLosses:
      spaceDemandOptions.airingLossInKwhPerHeatedArea * atemp,
    airLeakageLosses: toKwh(annualWhPerM2.airleakageHeat),
    activityHeating: toKwh(annualWhPerM2.activityHeat),
    occupantHeating: toKwh(annualWhPerM2.occupantHeat),
    solarInsolationHeating: toKwh(annualWhPerM2.insolationHeat),
    tapwaterHeatingDemand:
      spaceDemandOptions.tapwaterHeatingDemandKwhPerHeatedArea * atemp,
    tapWaterRecirculationGain: toKwh(
      (spaceDemandOptions.tapWaterRecirculationGainInWPerHeatedArea ?? 0) *
        HOURS_PER_YEAR,
    ), // Wh/m2 to kWh/m2
    tapWaterRecirculationLosses: toKwh(
      (spaceDemandOptions.tapWaterRecirculationLossesInWPerHeatedArea ?? 0) *
        HOURS_PER_YEAR,
    ), // Wh/m2 to kWh/m2
    heatingDemand: toKwh(annualWhPerM2.heat),
    postHeatingDemand: toKwh(annualWhPerM2.postHeat),
    coolingDemand: toKwh(annualWhPerM2.cool),
    preCoolingDemand: toKwh(annualWhPerM2.preCool),
    limitTemp: result.building.limitTemp ?? 999,
  });
};

/**
 * Cached building heating demand shared by the enrich pipeline and UI hooks.
 * @param version - The version of the building
 * @param options - See {@link BuildingHeatingDemandOptions}
 * @returns The heating demand for the building
 */
export const getBuildingHeatingDemand = (
  version: Baseline | undefined,
  options: BuildingHeatingDemandOptions,
): IHeatingDemandResult | HeatingDemandResultsWithErrors => {
  if (!version || options.environmentHours?.length !== HOURS_PER_YEAR) {
    return emptyHeatingDemandResult;
  }

  const proposalId = options.proposal?.id ?? '';
  return cacheFactory(
    () => computeBuildingHeatingDemand(version, options),
    `getBuildingHeatingDemand[${version.id}][${proposalId}]`,
    [
      version,
      options.metadata,
      options.environmentHours,
      options.heatedArea,
      options.design,
      options.proposal,
    ],
  );
};

/**
 * @deprecated Use useDesignNia instead (or similar)
 * Get heated area (Atemp) typically gfa * 0.9
 * @param gfaOrMeta gfa or project metadata
 * @returns
 */
export const getHeatedArea = (gfaOrMeta: number | ProjectMetadata) =>
  (typeof gfaOrMeta === 'number' ? gfaOrMeta : getBuildingGFA(gfaOrMeta)) * 0.9;

export const isSingleFamilyHouse = (heatedArea: number) => heatedArea <= 130;

/**
 * Get tap water pipe length (m) based on heated area
 * @param heatedArea
 * @returns Pipe length in meters
 */
export const getTapWaterPipeLength = (
  heatedArea: number,
  activity: EnergyActivity,
) => {
  // Often one tap water system per 1000m2 for offices so very low pipe length
  if (activity === ActivityType.Office || activity === ActivityType.School) {
    return heatedArea * 0.02;
  }
  return heatedArea * 0.1;
};

/**
 * Get tap water recirculation loss (in W)
 * @param pipeLength in meters
 * @returns Tap water recirculation loss in W
 */
export const getTapWaterRecirculationLossInW = (
  pipeLength: number,
  lossPerMeter: number = TAP_WATER_RECIRCULATION_LOSS,
): number => pipeLength * lossPerMeter;

/**
 * Get how many kWh/m2 of tapwater heating is needed for the activity every year.
 * Based on number from Page 4-9: https://rinfo.boverket.se/BFS2016-12/pdf/BFS2017-6.pdf
 * @param activity EnergyActivity
 * @param options Residential program subtype for single-family vs apartment rate
 * @returns Yearly tapwater heating demand in kWh/m2
 */
export const getTapwaterHeatingDemandByActivity = (
  activity: EnergyActivity = ActivityType.Residential,
  options: TapwaterHeatingDemandOptions = {},
) => {
  if (activity === ActivityType.Office) {
    return TAPWATER_HEATING_DEMAND.OFFICE;
  }
  if (activity === ActivityType.School) {
    return TAPWATER_HEATING_DEMAND.SCHOOL;
  }

  if (activity === ActivityType.Residential) {
    return options.residentialProgramType ===
      ResidentialProgramType.SingleFamily
      ? TAPWATER_HEATING_DEMAND.SMALL_HOUSE
      : TAPWATER_HEATING_DEMAND.APARTMENT;
  }

  return TAPWATER_HEATING_DEMAND.APARTMENT;
};

/**
 * Whether the energy report slide has B6 consumption to show for {@link proposal}.
 * False when {@link version} is missing or no resolved element carries `co2e_B6`.
 */
export const proposalHasEnergyConsumption = (
  version: Baseline | undefined,
  proposal: IProposal,
): boolean => {
  if (!version) return false;
  const record = proposal.resultsRecord ?? {};

  const elements = flattenElements(
    getResolvedBuildingVersion(version, proposal.id),
  );

  return elements
    .filter(projectGuards.is.element)
    .some((e) => record[e.id]?.co2e_B6);
};

/**
 * Check if an element should be included in the space demand calculation
 * Ignore temporary element since it's not part of the calculations
 * @param el
 * @returns
 */
export const isSpaceDemandElement = (el: OneOfElements): boolean =>
  isElementOfCategory(el, ...SPACE_DEMAND_CATEGORIES) &&
  !isTemporaryElementId(el.id);

/**
 * Check if the project requires weather hours to be calculated before enriching
 * @param project
 * @returns
 */
export const enrichRequiresWeatherHours = (project: Project): boolean => {
  const element = findElement(project, (e) => isSpaceDemandElement(e));
  return guards.is.defined(element);
};
